import React, { useState, useEffect } from "react";
import FirmAdviserListItem from "./FirmAdviserListItem";
import Button from "../../Inputs/Button";
import { useRouter } from "next/router";
import Link from "next/link";
import { FIRM_TYPES } from "../../../__constants__";
import * as analytics from "../../../analytics";
import ComponentWrapper from "./ComponentWrapper";
import { formatPlural } from "../../../utils/utilities";
import UnclaimedOrEmpty from "./UnclaimedOrEmpty";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

const ShowAllAdvisersButton = ({ adviserNumber, firmOrNetworkType, displayAsLink = false, dataTest, profile }) => { 
  const router = useRouter();
  const params = router.query;

  return (
    <div className={`flex justify-center items-center ${displayAsLink ? 'pb-5' : 'h-[330px]'}`}>
      <Link
        href={`/${firmOrNetworkType}/${params.firmPermalink}/advisers`}
        passHref
      >
        {displayAsLink
        ?
          <a data-test={dataTest} className="no-underline text-lg pt-2 hover:text-primary-blue">Show all {adviserNumber} {profile.type === FIRM_TYPES.COACH ? 'coaches' : 'advisers'}</a>
        :
          <Button
            text={`Show all ${adviserNumber} ${profile.type === FIRM_TYPES.COACH ? 'coaches' : 'advisers'}`}
            dataTest={dataTest}
            className="font-semibold py-1"
          />
        }
      </Link>
    </div>
  );
};

const AdviserCarousel = (props) => {
  const { advisers, profile, total, firmOrNetworkType, unclaimed, firmId, claimProfileUrl } = props;
  const verifiedCount = _.get(profile, ["statistics", "verified_advisers"], 0);
  const reviewCount = _.get(profile, ["statistics", "review_count"], 0);
  const [advisersToShow, setAdvisersToShow] = useState([]);
  const [showReadMoreButton, setShowReadMoreButton] = useState(false);
  const numberOfAdvisersInCarousel = 5;

  useEffect(() => {
    if (advisers && advisers?.length) {
      if (advisers.length <= numberOfAdvisersInCarousel) {
        setAdvisersToShow(advisers);
        setShowReadMoreButton(false);
      } else {
        setAdvisersToShow([...advisers.slice(0, numberOfAdvisersInCarousel)]);
        setShowReadMoreButton(true);
      }
    }
  }, [advisers, numberOfAdvisersInCarousel]);

  const handleCarouselNavigationBack = () => {
    analytics.track("Clicked adviser carousel back button", {
      category:
        firmOrNetworkType === "firm" ? "Firm Profile" : "Network Profile",
    })
  }

  const handleCarouselNavigationForward = () => {
    analytics.track("Clicked adviser carousel next button", {
      category:
        firmOrNetworkType === "firm" ? "Firm Profile" : "Network Profile",
    })
  }

  const handleAfterCarouselSlide = (previousSlide, { currentSlide }) => {
    const didClickRightArrow = currentSlide > previousSlide
    
    if (didClickRightArrow) {
      handleCarouselNavigationForward()
      return;
    }

    handleCarouselNavigationBack()
  }

  const getTitleText = () => {
    return `${total} ${
      profile.type === FIRM_TYPES.COACH
        ? formatPlural(total, "coach", "coaches")
        : formatPlural(total, "adviser", "advisers")
    } at ${profile.name} (${verifiedCount} with ${formatPlural(
      reviewCount,
      "review",
      "reviews"
    )})`;
  };

  const profileType = profile.type === FIRM_TYPES.COACH ? 'Coaches' : 'Advisers';

  return (
    <div className="bg-white mt-10 mb-11 max-xs:mb-4 max-xs:mt-4" id="advisers">
      {!unclaimed && advisersToShow.length > 0 ? (
        <>
          <div className="text-center">
            <h5 className="pb-10 text-md max-xs:px-4 max-xs:pb-4 sm:text-[1.5rem] text-[1.2rem] font-bold leading-[1.334] m-0">
              {getTitleText()}
            </h5>
          </div>

          <Carousel
            ssr={true}
            arrows={advisersToShow.length > 1}
            showDots={false}
            afterChange={handleAfterCarouselSlide}
            autoPlay={false}
            centerMode={false}
            className={advisersToShow.length < 3 ? 'justify-center max-xs:[&_*]:text-sm' : 'max-xs:[&_*]:!text-sm max-xs:[&_li>div]:px-4'}
            infinite={false}
            customTransition="transform 600ms ease-in-out"
            responsive={{
              desktop: {
                breakpoint: { max: 4000, min: 960 },
                items: 3,
                slidesToSlide: 3
              },
              tablet: {
                breakpoint: { max: 960, min: 600 },
                items: 2,
                slidesToSlide: 2
              },
              mobile: {
                breakpoint: { max: 600, min: 0 },
                items: 1,
                slidesToSlide: 1
              }
          }}>
            {advisersToShow.map((adviser) => (
              <FirmAdviserListItem
                key={adviser.id}
                adviser={adviser}
                firmOrNetworkType={firmOrNetworkType}
                isCarouselItem={true}
              />
            ))}

            {showReadMoreButton && 
              <ShowAllAdvisersButton
                firmOrNetworkType={firmOrNetworkType}
                adviserNumber={total}
                profile={profile}
                dataTest="firm-adviser-show-all-in-carousel-button"
              />
            }
          </Carousel>

          {showReadMoreButton &&
            <ShowAllAdvisersButton
              firmOrNetworkType={firmOrNetworkType}
              adviserNumber={total}
              displayAsLink
              profile={profile}
              dataTest="firm-adviser-show-all-below-carousel-button"
            />
          }
        </>
      ) : (
        <ComponentWrapper>
          <h5 className="pt-8 text-[1.5rem] font-bold leading-[1.334] m-0 max-xs:text-center">
            {`${profileType} at ${profile.name}`}
          </h5>
          <div className="pb-4">
            <UnclaimedOrEmpty
              text={`${profile.name} have not confirmed their ${profileType.toLowerCase()} yet.`}
              subtext={`Do you work at ${profile.name}?`}
              firmId={firmId}
              linkText={`Add ${profileType.toLowerCase()}`}
              unclaimed={unclaimed}
              claimProfileUrl={claimProfileUrl}
              utmTerm="advisers"
            />
          </div>
        </ComponentWrapper>
      )}
    </div>
  );
};

export default AdviserCarousel;
